/**
 * The motion budget (D-6) — when the water may move, as a small controller
 * with its clock and its animation frame injected, so every rule is testable.
 *
 * **Motion on input, never on a clock.** Nothing moves until `wake()` — a
 * screen opening, an expense logged, a preview nudged. Then it runs only while
 * the surface is actually still moving (the `frame` callback says so) and
 * never longer than `maxMs` after the last input; the closing frame is an
 * unbounded step, so the surface freezes exactly at its target, never a hair
 * off. Returning to the app, a resize or a theme change repaint one still
 * frame (`paintStill`) and start nothing.
 *
 * **Never while hidden** — `wake()` schedules nothing for a hidden document,
 * and `stop()` cancels the loop outright. **Never under reduced motion** —
 * `wake()` draws the one still frame and returns. Battery saver has no web
 * signal of its own; where an OS's saver turns on "remove animations", it
 * arrives as reduced motion and is honoured here.
 */

/** The longest step one frame may take, so a stalled tab does not jump the water. */
export const MAX_FRAME_STEP_MS = 64;
/** D-6: no single input moves the water for longer than this. */
export const MOTION_BUDGET_MS = 4_000;

export interface MotionBudget {
  /** An input happened: move until settled, at most the budget from now. */
  wake(): void;
  /** Redraw where things are, without motion. */
  paintStill(): void;
  /** Cancel the loop entirely. */
  stop(): void;
  running(): boolean;
}

export function createMotionBudget(options: {
  /** Draw one frame `dtMs` after the last; return true while still moving. Infinity means "snap". */
  frame: (dtMs: number) => boolean;
  requestFrame: (callback: (timeMs: number) => void) => number;
  cancelFrame: (id: number) => void;
  now: () => number;
  isHidden: () => boolean;
  reducedMotion: () => boolean;
  maxMs?: number;
}): MotionBudget {
  const maxMs = options.maxMs ?? MOTION_BUDGET_MS;
  let raf = 0;
  let last = 0;
  let deadline = 0;

  const tick = (timeMs: number): void => {
    raf = 0;
    const dt = Math.min(Math.max(timeMs - last, 0), MAX_FRAME_STEP_MS);
    last = timeMs;
    if (timeMs >= deadline) {
      options.frame(Number.POSITIVE_INFINITY);
      return;
    }
    if (options.frame(dt)) raf = options.requestFrame(tick);
  };

  return {
    wake(): void {
      if (options.reducedMotion()) {
        options.frame(Number.POSITIVE_INFINITY);
        return;
      }
      if (options.isHidden()) return;
      deadline = options.now() + maxMs;
      if (raf !== 0) return;
      last = options.now();
      raf = options.requestFrame(tick);
    },
    paintStill(): void {
      if (raf !== 0) return; // already moving; the next frame draws the change
      options.frame(Number.POSITIVE_INFINITY);
    },
    stop(): void {
      if (raf !== 0) options.cancelFrame(raf);
      raf = 0;
    },
    running(): boolean {
      return raf !== 0;
    },
  };
}
